import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  ArrowLeft,
  Flame,
  Trophy,
  BookOpen,
  Brain,
  Target,
  Sparkles,
  Award,
  Users,
  UserPlus,
  Loader2,
  ChevronRight,
  Lock,
  Snowflake
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { usePreferences } from '../context/PreferencesContext';

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  isActiveToday: boolean;
  freezesAvailable: number;
  freezesUsed: number;
  isFreezeActive: boolean;
}

interface Milestone {
  days: number;
  name: string;
  achieved: boolean;
  achievedAt?: string;
}

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  points: number;
  earnedAt?: string;
}

interface AchievementsData {
  earned: Achievement[];
  locked: Achievement[];
  totalPoints: number;
}

interface OrientationProfile {
  archetype_primary: string;
  archetype_secondary: string;
  big5_openness: number;
  big5_conscientiousness: number;
  big5_extraversion: number;
  big5_agreeableness: number;
  big5_stability: number;
  riasec_primary: string;
  riasec_secondary: string;
  riasec_tertiary: string;
  ls_structure: number;
  ls_solo_collab: number;
  ls_pace: number;
  ls_abstraction: number;
}

interface ProgressStats {
  lessonsCompleted: number;
  lessonsInProgress: number;
  tracksStarted: number;
}

const ARCHETYPE_INFO: Record<string, { emoji: string; tagline: string; color: string }> = {
  Explorer: { emoji: '🔭', tagline: 'Curious mind, endless questions', color: 'text-purple-400' },
  Builder: { emoji: '🔧', tagline: 'Make it work, make it better', color: 'text-orange-400' },
  Analyst: { emoji: '📊', tagline: 'Data drives decisions', color: 'text-blue-400' },
  Connector: { emoji: '🤝', tagline: 'People first, always', color: 'text-green-400' },
  Creator: { emoji: '🎨', tagline: 'Imagination without limits', color: 'text-pink-400' },
  Strategist: { emoji: '♟️', tagline: 'See the bigger picture', color: 'text-yellow-400' }
};

const RIASEC_LABELS: Record<string, string> = {
  R: 'Realistic',
  I: 'Investigative',
  A: 'Artistic',
  S: 'Social',
  E: 'Enterprising',
  C: 'Conventional'
};

export default function Profile() {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const { preferences } = usePreferences();

  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [achievements, setAchievements] = useState<AchievementsData | null>(null);
  const [profile, setProfile] = useState<OrientationProfile | null>(null);
  const [progressStats, setProgressStats] = useState<ProgressStats>({ lessonsCompleted: 0, lessonsInProgress: 0, tracksStarted: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfileData();
  }, []);

  async function loadProfileData() {
    try {
      const [streakRes, milestonesRes, achievementsRes, profileRes, progressRes] = await Promise.all([
        fetchApi<{ streak: StreakData }>('/api/streaks/me').catch(() => ({ streak: null })),
        fetchApi<{ milestones: Milestone[] }>('/api/streaks/milestones').catch(() => ({ milestones: [] })),
        fetchApi<AchievementsData>('/api/achievements/me').catch(() => ({ earned: [], locked: [], totalPoints: 0 })),
        fetchApi<{ profile: OrientationProfile }>('/api/orientation/profile').catch(() => ({ profile: null })),
        fetchApi<{ progress: Array<{ status: string; track_slug: string }> }>('/api/progress').catch(() => ({ progress: [] }))
      ]);

      setStreakData(streakRes.streak);
      setMilestones(milestonesRes.milestones || []);
      setAchievements(achievementsRes);
      setProfile(profileRes.profile);

      // Calculate progress stats
      const progress = progressRes.progress || [];
      const completed = progress.filter(p => p.status === 'COMPLETED').length;
      const inProgress = progress.filter(p => p.status === 'IN_PROGRESS').length;
      const tracks = new Set(progress.map(p => p.track_slug)).size;
      setProgressStats({ lessonsCompleted: completed, lessonsInProgress: inProgress, tracksStarted: tracks });

    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  const archetypeInfo = profile?.archetype_primary ? ARCHETYPE_INFO[profile.archetype_primary] : null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Link */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      {/* Profile Header */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-6 mb-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-accent-blue to-purple-600 flex items-center justify-center text-3xl font-bold text-white shrink-0">
            {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-white truncate">
              {user?.name || 'Learner'}
            </h1>
            <p className="text-gray-400 text-sm truncate">{user?.email}</p>

            {/* Archetype Badge */}
            {archetypeInfo && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-full">
                <span className="text-xl">{archetypeInfo.emoji}</span>
                <span className={`font-medium ${archetypeInfo.color}`}>
                  {profile?.archetype_primary}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-400 text-sm">{archetypeInfo.tagline}</span>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="flex gap-6 text-center shrink-0">
            <div>
              <div className="text-2xl font-bold text-accent-green">{progressStats.lessonsCompleted}</div>
              <div className="text-xs text-gray-400">Completed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-400">{streakData?.currentStreak || 0}</div>
              <div className="text-xs text-gray-400">Day Streak</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-400">{achievements?.totalPoints || 0}</div>
              <div className="text-xs text-gray-400">Points</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Streak & Milestones */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-400" />
            Streak Progress
          </h2>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-orange-400">{streakData?.currentStreak || 0}</span>
                <span className="text-gray-400">day streak</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Longest: {streakData?.longestStreak || 0} days
              </p>
            </div>

            {streakData?.freezesAvailable !== undefined && streakData.freezesAvailable > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <Snowflake className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-400">
                  {streakData.freezesAvailable} freeze{streakData.freezesAvailable !== 1 ? 's' : ''} available
                </span>
              </div>
            )}
          </div>

          {/* Milestone Progress */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-300">Milestones</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { days: 7, name: 'Week Warrior', icon: '🔥' },
                { days: 30, name: 'Month Master', icon: '⭐' },
                { days: 100, name: 'Century Champion', icon: '💯' },
                { days: 365, name: 'Year Legend', icon: '👑' }
              ].map(milestone => {
                const achieved = milestones.find(m => m.days === milestone.days)?.achieved;
                return (
                  <div
                    key={milestone.days}
                    className={`p-3 rounded-lg text-center ${
                      achieved
                        ? 'bg-orange-500/20 border border-orange-500/30'
                        : 'bg-gray-700/50 border border-gray-700'
                    }`}
                  >
                    <div className="text-2xl mb-1">{achieved ? milestone.icon : '🔒'}</div>
                    <div className={`text-xs font-medium ${achieved ? 'text-orange-400' : 'text-gray-500'}`}>
                      {milestone.days}d
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Achievements
            </h2>
            <span className="text-sm text-gray-400">
              {achievements?.earned.length || 0} / {(achievements?.earned.length || 0) + (achievements?.locked.length || 0)} unlocked
            </span>
          </div>

          {achievements && achievements.earned.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {achievements.earned.slice(0, 8).map(achievement => (
                <div
                  key={achievement.id}
                  className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center group relative"
                  title={`${achievement.name}: ${achievement.description}`}
                >
                  <div className="text-2xl mb-1">{achievement.icon || '🏆'}</div>
                  <div className="text-xs text-yellow-400 truncate">{achievement.name}</div>
                </div>
              ))}
              {achievements.earned.length > 8 && (
                <div className="p-3 bg-gray-700/50 border border-gray-700 rounded-lg text-center flex items-center justify-center">
                  <span className="text-sm text-gray-400">+{achievements.earned.length - 8} more</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <Trophy className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Complete lessons to earn achievements!</p>
            </div>
          )}

          {/* Next Achievement Preview */}
          {achievements && achievements.locked.length > 0 && (
            <div className="mt-4 p-3 bg-gray-700/30 rounded-lg border border-gray-700">
              <p className="text-xs text-gray-500 mb-1">Next to unlock:</p>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-300">{achievements.locked[0].name}</span>
                <span className="text-xs text-gray-500">- {achievements.locked[0].description}</span>
              </div>
            </div>
          )}
        </div>

        {/* Learning Profile */}
        {profile ? (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              Learning Profile
            </h2>

            {/* Archetypes */}
            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-2">Your Archetypes</p>
              <div className="flex gap-2">
                {profile.archetype_primary && (
                  <span className={`px-3 py-1 rounded-full bg-gray-700/50 text-sm font-medium ${ARCHETYPE_INFO[profile.archetype_primary]?.color || 'text-white'}`}>
                    {ARCHETYPE_INFO[profile.archetype_primary]?.emoji} {profile.archetype_primary}
                  </span>
                )}
                {profile.archetype_secondary && (
                  <span className={`px-3 py-1 rounded-full bg-gray-700/50 text-sm ${ARCHETYPE_INFO[profile.archetype_secondary]?.color || 'text-gray-300'}`}>
                    {ARCHETYPE_INFO[profile.archetype_secondary]?.emoji} {profile.archetype_secondary}
                  </span>
                )}
              </div>
            </div>

            {/* RIASEC */}
            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-2">Interest Profile (RIASEC)</p>
              <div className="flex gap-2 text-sm">
                {[profile.riasec_primary, profile.riasec_secondary, profile.riasec_tertiary].filter(Boolean).map((code, i) => (
                  <span key={code} className={`px-2 py-1 rounded ${i === 0 ? 'bg-accent-blue/20 text-accent-blue' : 'bg-gray-700 text-gray-300'}`}>
                    {RIASEC_LABELS[code!] || code}
                  </span>
                ))}
              </div>
            </div>

            {/* Learning Style Bars */}
            <div className="space-y-3">
              <p className="text-sm text-gray-400">Learning Preferences</p>

              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Exploratory</span>
                  <span>Structured</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
                    style={{ width: `${((profile.ls_structure || 3) / 5) * 100}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Solo</span>
                  <span>Collaborative</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-teal-500 rounded-full"
                    style={{ width: `${((profile.ls_solo_collab || 3) / 5) * 100}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Steady Pace</span>
                  <span>Intensive Bursts</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                    style={{ width: `${((profile.ls_pace || 3) / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              Learning Profile
            </h2>
            <div className="text-center py-6">
              <Target className="w-10 h-10 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400 mb-4">Discover your learning archetype</p>
              <Link
                to="/orientation"
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent-blue hover:bg-accent-blue/80 text-slate-900 rounded-lg transition-colors"
              >
                Take Assessment
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Connection & Progress */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent-blue" />
            Learning Journey
          </h2>

          {/* Progress Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-3 bg-gray-700/30 rounded-lg">
              <BookOpen className="w-5 h-5 mx-auto mb-1 text-green-400" />
              <div className="text-xl font-bold text-white">{progressStats.lessonsCompleted}</div>
              <div className="text-xs text-gray-400">Lessons Done</div>
            </div>
            <div className="text-center p-3 bg-gray-700/30 rounded-lg">
              <Target className="w-5 h-5 mx-auto mb-1 text-blue-400" />
              <div className="text-xl font-bold text-white">{progressStats.lessonsInProgress}</div>
              <div className="text-xs text-gray-400">In Progress</div>
            </div>
            <div className="text-center p-3 bg-gray-700/30 rounded-lg">
              <Award className="w-5 h-5 mx-auto mb-1 text-purple-400" />
              <div className="text-xl font-bold text-white">{progressStats.tracksStarted}</div>
              <div className="text-xs text-gray-400">Tracks Started</div>
            </div>
          </div>

          {/* Connection Mode */}
          <div className="p-4 bg-gray-700/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {preferences.connection_mode === 'solo' && <User className="w-5 h-5 text-gray-400" />}
                {preferences.connection_mode === 'silent_cohort' && <Users className="w-5 h-5 text-purple-400" />}
                {preferences.connection_mode === 'buddy' && <UserPlus className="w-5 h-5 text-green-400" />}
                <div>
                  <p className="text-sm font-medium text-white capitalize">
                    {preferences.connection_mode === 'silent_cohort' ? 'Silent Cohort' : preferences.connection_mode} Mode
                  </p>
                  <p className="text-xs text-gray-500">
                    {preferences.connection_mode === 'solo' && 'Learning independently'}
                    {preferences.connection_mode === 'silent_cohort' && 'Connected anonymously with peers'}
                    {preferences.connection_mode === 'buddy' && 'Paired with an accountability buddy'}
                  </p>
                </div>
              </div>
              <Link
                to="/preferences"
                className="text-sm text-accent-blue hover:text-accent-blue/80 transition-colors"
              >
                Change
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 flex justify-center gap-4">
        <Link
          to="/preferences"
          className="text-sm text-gray-400 hover:text-white transition-colors"
        >
          Edit Preferences
        </Link>
        <span className="text-gray-700">•</span>
        <Link
          to="/orientation"
          className="text-sm text-gray-400 hover:text-white transition-colors"
        >
          Retake Assessment
        </Link>
      </div>
    </div>
  );
}
